import React from 'react'
import { useStaticQuery, graphql, Link } from 'gatsby'
import { getImage } from "gatsby-plugin-image"
import { Menu, Transition } from '@headlessui/react'
import { RichText } from 'prismic-reactjs';
import htmlSerializer from '../../utils/htmlSerializer';
import loadable from '@loadable/component'
import pMinDelay from 'p-min-delay'

import UpdateLogoVersion from '../common/UpdateLogoVersion';
import ContactImageVersion from '../common/ContactImageVersion';
import SearchButton from '../search/SearchButton'
import MobileDrawerNew from './MobileDrawerNew';

const AnnouncementText = loadable(() => import('./AnnouncementText'));

const Search = loadable(() => pMinDelay(import('../search'), 3000))

const searchIndices = [{ name: `Products`, title: `Products` }]

const menuLink = 'font-display font-medium text-lg text-red-100 uppercase tracking-widest hover:underline cursor-pointer'

const HeaderNavNewVersionNew = () => {
  const data = useStaticQuery(graphql`
    query HeaderNavNewVersionNewQueryNew {
      prismicHomePage {
        data {
          announcement_section_active
        }
      }
      prismicNavigation {
        data {
          header_logo {
            gatsbyImageData(
              width: 300
            )
          }
          header_title {
           richText
          }
          show_home_menu
          about_menu_name
          contact_menu_name
          contact_image {
            gatsbyImageData(
              width: 300
            )
          }
          contact_image_phone_number
          first_product_category_name
          first_product_category_description
          second_product_category_name
          second_product_category_description
          third_product_category_name
          third_product_category_description
          fourth_product_category_name
          fourth_product_category_description
          fifth_product_category_name
          fifth_product_category_description
          sixth_product_category_name
          sixth_product_category_description
          seventh_product_category_name
          seventh_product_category_description
          eight_product_category_name
          eight_product_category_description
          ninth_product_category_name
          ninth_product_category_description
          tenth_product_category_name
          tenth_product_category_description
          first_resource_name
          first_resource_description
          second_resource_name
          second_resource_description
          third_resource_name
          third_resource_description
          fourth_resource_name
          fourth_resource_description
          fifth_resource_name
          fifth_resource_description
        }
      }
    }
  `)

  const doc = data.prismicHomePage.data
  const nav = data.prismicNavigation.data
  const logo = getImage(nav.header_logo)
  const contactImg = getImage(nav.contact_image)

  const products = [
    { name: nav.first_product_category_name, link: '/command-packages', desc: nav.first_product_category_description },
    { name: nav.second_product_category_name, link: '/low-energy-operator', desc: nav.second_product_category_description },
    { name: nav.third_product_category_name, link: '/mortise-locks', desc: nav.third_product_category_description },
    { name: nav.fourth_product_category_name, link: '/cylindrical-locks', desc: nav.fourth_product_category_description },
    { name: nav.fifth_product_category_name, link: '/exit-device-kits', desc: nav.fifth_product_category_description },
    { name: nav.sixth_product_category_name, link: '/exit-devices', desc: nav.sixth_product_category_description },
    { name: nav.seventh_product_category_name, link: '/exit-trims', desc: nav.seventh_product_category_description },
    { name: nav.eight_product_category_name, link: '/power-supplies', desc: nav.eight_product_category_description },
    { name: nav.ninth_product_category_name, link: '/power-transfers', desc: nav.ninth_product_category_description },
    { name: nav.tenth_product_category_name, link: '/accessories', desc: nav.tenth_product_category_description },
  ]

  const resources = [
    { name: nav.first_resource_name, link: '/forms', desc: nav.first_resource_description },
    { name: nav.second_resource_name, link: '/distributors', desc: nav.second_resource_description },
    { name: nav.third_resource_name, link: '/knowledgebase', desc: nav.third_resource_description },
    { name: nav.fourth_resource_name, link: '/sales-representatives', desc: nav.fourth_resource_description },
    { name: nav.fifth_resource_name, link: '/blog', desc: nav.fifth_resource_description },
  ]

  return (
    <>
      {doc.announcement_section_active
        ?
        <div className='bg-gray-900 z-50'>
          <div className="w-11/12 xl:max-w-screen-xl h-20 md:h-12 flex items-center justify-center lg:py-2 py-5 mx-auto">
            <AnnouncementText />
          </div>
        </div>
        :
        null
      }

      <div className='bg-red-900 border-b-0 xl:border-b xl:border-gray-900 border-t-4 xl:border-t-0 border-red-900 z-50'>
        <nav className="w-11/12 xl:max-w-screen-xl flex items-center justify-between bg-red-900 lg:py-2 py-5 mx-auto">
          <div className="w-40 h-auto hidden xl:block mr-6">
            <Link to="/">
              <UpdateLogoVersion imgData={logo} />
            </Link>
          </div>

          <div className="w-24 h-auto xl:hidden">
            <Link to="/">
              <UpdateLogoVersion imgData={logo} />
            </Link>
          </div>

          <div className="hidden xl:flex flex-col flex-grow mx-16">
            <div className='header-title relative'>
              {nav.header_title 
                ?
                <RichText
                  render={nav.header_title.richText}
                  htmlSerializer={htmlSerializer}
                />
                :
                <h2 className="font-display text-red-100 text-2xl uppercase text-center tracking-widest">
                  Manufacturer of <span className="text-3xl font-black">Electrified</span> Locking Hardware
                </h2>
              }
            </div>

            <hr className="border border-red-700" />

            <div className="flex items-center justify-between mt-2 mx-4">
              {nav.show_home_menu
                ?
                <Link to="/" className={menuLink}>
                  Home
                </Link>
                :
                null
              }

              <div className="relative">
                <Menu>
                  {({ open }) => (
                    <>
                      <Menu.Button className="group inline-flex items-center space-x-1 focus:outline-none">
                        <span className='font-display font-medium text-lg text-red-100 uppercase tracking-widest cursor-pointer'>Products</span>
                        <svg className="text-white fill-current h-5 w-5 group-hover:text-white group-focus:text-white transition ease-in-out duration-150" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                        </svg>
                      </Menu.Button>

                      {/* Use the Transition + open render prop argument to add transitions. */}
                      <Transition
                        show={open}
                        enter="transition ease-out duration-200"
                        enterFrom="transform opacity-0 translate-y-1"
                        enterTo="transform opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="transform opacity-100 translate-y-0"
                        leaveTo="transform opacity-0 translate-y-1"
                      >
                        <Menu.Items static className="absolute -ml-4 mt-3 transform focus:outline-none w-screen max-w-2xl z-50">
                        <div className="rounded-lg shadow-lg">
                          <div className="rounded-lg shadow-xs overflow-hidden">
                            <div className="z-20 relative grid grid-cols-2 gap-8 bg-white px-5 py-6">
                              {products.map((product, index) => {
                                return (
                                  <Link
                                    to={product.link}
                                    key={index}
                                    className="-m-3 p-3 flex items-start space-x-4 rounded-lg hover:bg-gray-200 transition ease-in-out duration-150"
                                  >
                                    <Menu.Item className="space-y-1">
                                      <div className='flex flex-col'>
                                        <p className="text-base leading-6 font-bold text-red-900">
                                          {product.name}
                                        </p>
                                        <p className="text-sm leading-5 text-gray-500">
                                          {product.desc}
                                        </p>
                                      </div>
                                    </Menu.Item>
                                  </Link>
                                )
                              })}
                            </div>
                          </div>
                        </div>
                        </Menu.Items>
                      </Transition>
                    </>
                  )}
                </Menu>
              </div>

              <div className="relative">
                <Menu>
                  {({ open }) => (
                    <>
                      <Menu.Button className="group inline-flex items-center space-x-1 focus:outline-none">
                        <span className='font-display font-medium text-lg text-red-100 uppercase tracking-widest cursor-pointer'>Resources</span>
                        <svg className="text-white fill-current h-5 w-5 group-hover:text-white group-focus:text-white transition ease-in-out duration-150" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                        </svg>
                      </Menu.Button>

                      {/* Use the Transition + open render prop argument to add transitions. */}
                      <Transition
                        show={open}
                        enter="transition ease-out duration-200"
                        enterFrom="transform opacity-0 translate-y-1"
                        enterTo="transform opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="transform opacity-100 translate-y-0"
                        leaveTo="transform opacity-0 translate-y-1"
                      >
                        <Menu.Items static className="absolute -ml-4 mt-3 transform focus:outline-none w-screen max-w-2xl z-50">
                        <div className="rounded-lg shadow-lg">
                          <div className="rounded-lg shadow-xs overflow-hidden">
                            <div className="z-20 relative grid grid-cols-2 gap-8 bg-white px-5 py-6">
                              {resources.map((product, index) => {
                                return (
                                  <Link
                                    to={product.link}
                                    key={index}
                                    className="-m-3 p-3 flex items-start space-x-4 rounded-lg hover:bg-gray-200 transition ease-in-out duration-150"
                                  >
                                    <Menu.Item className="space-y-1">
                                      <div className='flex flex-col'>
                                        <p className="text-base leading-6 font-bold text-red-900">
                                          {product.name}
                                        </p>
                                        <p className="text-sm leading-5 text-gray-500">
                                          {product.desc}
                                        </p>
                                      </div>
                                    </Menu.Item>
                                  </Link>
                                )
                              })}
                            </div>
                          </div>
                        </div>
                        </Menu.Items>
                      </Transition>
                    </>
                  )}
                </Menu>
              </div>

              <Link to="/about" className={menuLink}>
                {nav.about_menu_name}
              </Link>

              <Link to="/contact" className={menuLink}>
                {nav.contact_menu_name}
              </Link>

              <SearchButton />
            </div>
          </div>

          <div className="hidden xl:flex items-center w-auto">
            <a href={`tel:${nav.contact_image_phone_number}`}>
              <ContactImageVersion imgData={contactImg} />
            </a>
          </div>

          <div className='xl:hidden relative flex flex-grow items-center justify-end'>
            <div className='absolute'>
              <Search indices={searchIndices} />
            </div>
          </div>

          <MobileDrawerNew />
        </nav>
      </div>
    </>
  )
}

export default HeaderNavNewVersionNew